<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import Select from 'primevue/select'
import api from '@/api'
import { useI18n } from '@/i18n'
import { useFormatDate } from '@/composables/useFormatDate'

const props = defineProps({
  groupId: { type: [String, Number], required: false }
})
const route   = useRoute()
const { t }   = useI18n()
const { fmtDate } = useFormatDate()
const groupId = computed(() => Number(props.groupId ?? route.params.id))

const loading  = ref(false)
const error    = ref('')
const tools    = ref([])
const total    = ref(0)
const category = ref(null)

const columns = [
  { field: 'rank_in_group',  header: t('hackwatch.tools.colRank'),     sortable: true  },
  { field: 'tool_name',      header: t('hackwatch.tools.colTool'),     sortable: true  },
  { field: 'category_name',  header: t('hackwatch.tools.colCategory'), sortable: true  },
  { field: 'usage_links',    header: t('hackwatch.tools.colLinks'),    sortable: true  },
  { field: 'last_link_at',   header: t('hackwatch.tools.colLastLink'), sortable: true  },
]

const categoryOptions = computed(() => {
  const map = new Map()
  for (const r of tools.value) {
    if (r?.category_id != null) map.set(r.category_id, r.category_name || `#${r.category_id}`)
  }
  return Array.from(map, ([value, label]) => ({ label, value }))
})

async function fetchTools(q = {}) {
  if (!groupId.value) return
  loading.value = true
  error.value = ''
  try {
    const params = {
      page:        q.page  ?? 1,
      limit:       q.limit ?? 25,
      q:           q.search || undefined,
      category_id: category.value ?? undefined,
      sortBy:      q.sortField  || 'rank_in_group',
      sortDir:     q.sortOrder  ?? 'asc',
    }
    const { data } = await api.getHackerGroupToolById(groupId.value, { params })
    const list = Array.isArray(data?.tools)
      ? data.tools
      : Object.keys(data?.tools || {})
          .filter(k => !Number.isNaN(Number(k)))
          .sort((a, b) => Number(a) - Number(b))
          .map(k => data.tools[k])
    tools.value = list
    total.value = data?.total ?? list.length
  } catch (e) {
    console.error(e)
    error.value = e?.response?.data?.message || t('hackwatch.tools.loadFailed')
  } finally {
    loading.value = false
  }
}

onMounted(() => fetchTools())
watch(() => groupId.value, () => fetchTools())
watch(category, () => fetchTools({ page: 1 }))

function onQueryChange(q) { fetchTools(q) }
</script>

<template>
  <div class="hw-card">
    <div class="hw-card-header">
      <i class="pi pi-wrench hw-card-icon" />
      <span class="hw-card-title">{{ t('hackwatch.tools.title') }}</span>
    </div>
    <AppTableWidget
      :columns="columns"
      :data="tools"
      :total="total"
      :loading="loading"
      lazy
      :searchable="true"
      :default-rows="10"
      :rows-options="[10, 25, 50]"
      data-key="tool_id"
      :empty-text="t('hackwatch.tools.noTools')"
      @refresh="fetchTools()"
      @query-change="onQueryChange"
    >
      <template #toolbar>
        <Select
          v-model="category"
          :options="categoryOptions"
          optionLabel="label"
          optionValue="value"
          :placeholder="t('hackwatch.tools.categoryPlaceholder')"
          :showClear="true"
          size="small"
          class="w-12rem"
        />
      </template>

      <template #cell-rank_in_group="{ row }">
        <Tag :value="String(row.rank_in_group ?? '—')" severity="secondary" />
      </template>
      <template #cell-category_name="{ row }">
        <Tag :value="row.category_name || '—'" severity="secondary" />
      </template>
      <template #cell-last_link_at="{ row }">{{ fmtDate(row.last_link_at) }}</template>
    </AppTableWidget>

    <div v-if="error" class="mt-1 text-sm" style="color: var(--p-danger)">{{ error }}</div>
  </div>
</template>
